import { diffInMs, nowISO } from '~/utils/date';

import type { RunRead } from '../types/run.api.types';
import type { RunListTab } from '../types/runs.types';

/** Test-id prefix every table id derives from (DataTable adds `-row-<id>`, `-sort-<col>`). */
export const BATCHES_TABLE_TEST_ID = 'catalog-enrichment-batches-table';

/**
 * The Batches table's status views, in order: what waits on a person first, then what moves,
 * what stopped, what finished. Failed / Rejected only show when they hold something.
 */
export const BATCH_TABLE_VIEWS: readonly RunListTab[] = [
  'AWAITING_GATE',
  'RUNNING',
  'FAILED',
  'REJECTED',
  'COMPLETED',
  'ALL',
];

/** Views hidden while empty, so the bar doesn't carry "0" tabs. */
export const BATCH_TABLE_OPTIONAL_VIEWS: ReadonlySet<RunListTab> = new Set(['FAILED', 'REJECTED']);

/** A stopped batch stays red (and keeps its Retry button) this long after it last moved. */
export const STOPPED_RECENT_DAYS = 7;
const RECENT_MS = STOPPED_RECENT_DAYS * 24 * 60 * 60 * 1000;

/** When a batch last moved: its finish once it ended, else its last action, start or creation. */
export function lastActivityOf(run: RunRead): string {
  // A batch that ended is dated by when it ended: a later action (archive, restore) must not make
  // an old stop look recent again (the 7-day rule).
  if (run.completedAt) return run.completedAt;
  return run.lastActionAt ?? run.startedAt ?? run.audit.createdAt;
}

/** True when the batch moved within the last week — older failures read as history, not alarms. */
export function isRecentRun(run: RunRead): boolean {
  return diffInMs(lastActivityOf(run), nowISO()) < RECENT_MS;
}

/** Session key for the Needs you queue the batch page walks with "Next waiting batch". */
const NEEDS_YOU_QUEUE_KEY = 'catalog-enrichment:needs-you-queue';

/** Remembers the waiting batches in list order, so the batch page can offer the next one. */
export function saveNeedsYouQueue(runIds: readonly string[]) {
  try {
    sessionStorage.setItem(NEEDS_YOU_QUEUE_KEY, JSON.stringify(runIds));
  } catch {
    // Storage blocked: the batch page just shows no "Next" link.
  }
}

/** The waiting batch after `runId` in the saved queue, or null when it is last / not queued. */
export function nextInNeedsYouQueue(runId: string): string | null {
  try {
    const raw = sessionStorage.getItem(NEEDS_YOU_QUEUE_KEY);
    const ids = raw ? (JSON.parse(raw) as unknown) : null;
    if (!Array.isArray(ids)) return null;
    const at = ids.indexOf(runId);
    const next: unknown = at >= 0 ? ids[at + 1] : undefined;
    return typeof next === 'string' ? next : null;
  } catch {
    return null;
  }
}
